import "@khan/guide-ui/message-bubble.css";

import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";

import Button, {type ButtonProps} from "@khanacademy/wonder-blocks-button";
import {View} from "@khanacademy/wonder-blocks-core";
import {Spring} from "@khanacademy/wonder-blocks-layout";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {Body} from "@khanacademy/wonder-blocks-typography";
import {useUserAttributes} from "@khan/shared-core";

import type {guideTranscript_aiGuideThread as Thread} from "./guide-transcripts/__graphql-types__/guideTranscript.ts";
import {
    canResumeActivity,
    canResumeClassroomThread,
    getResumeUrl,
    isClassroomExperienceThread,
    isDeprecatedTutoringActivity,
    isExerciseThread,
} from "./utils.ts";

interface Props {
    thread: Thread;
    onClassroomExperience: boolean;
    buttonKind?: ButtonProps["kind"];
}

const MaybeResumeLink = ({
    thread,
    buttonKind = "secondary",
    onClassroomExperience,
}: Props): React.ReactElement => {
    const {interactions, id: threadId} = thread;
    const attributes = useUserAttributes();
    // Hide resume link if the logged-in user does not own the thread
    if (
        attributes == null ||
        attributes.kaid !== thread.kaid ||
        interactions.length === 0 ||
        !interactions[0].url
    ) {
        return <></>;
    }

    const firstInteractionURL = interactions[0].url;
    const pathname = new URL(firstInteractionURL).pathname;
    const activityCanBeResumed = canResumeActivity(pathname);
    let resumeURL = "";
    if (activityCanBeResumed) {
        resumeURL = getResumeUrl(
            firstInteractionURL,
            threadId,
            onClassroomExperience,
        );
    }

    if (!activityCanBeResumed) {
        return (
            <View style={styles.contextHeaderWrapper}>
                {/* Only display the resume chat link for activities where resuming chat is fully functional.
                Otherwise, display a message indicating resuming is not available. */}
                <View>
                    <Body>
                        {t`Restore chat is not currently supported for this activity - but we're working on it. Check back soon!`}
                    </Body>
                </View>
            </View>
        );
    }

    const threadFromCurrentExperience =
        onClassroomExperience === isClassroomExperienceThread(thread);

    const disabled =
        thread.flagged ||
        // Cannot resume threads that were not from current app
        !threadFromCurrentExperience ||
        // Cannot resume exercise threads on either experience
        isExerciseThread(thread) ||
        // Cannot resume exercise, article or video threads on classroom
        !canResumeClassroomThread(thread) ||
        // Cannot resume a tutoring activity that is considered deprecated
        isDeprecatedTutoringActivity(pathname);

    return (
        <View style={styles.contextHeaderWrapper}>
            <View>
                <Spring />
                {disabled ? (
                    <Tooltip content={t`This thread cannot be resumed`}>
                        <Button
                            testId="restoreChat"
                            kind={buttonKind}
                            disabled={true}
                        >
                            {t`Resume chat`}
                        </Button>
                    </Tooltip>
                ) : (
                    <Button
                        href={resumeURL}
                        target="_blank"
                        testId="restoreChat"
                        kind="secondary"
                    >
                        {t`Resume chat`}
                    </Button>
                )}
                <Spring />
            </View>
        </View>
    );
};

export default MaybeResumeLink;

const styles = StyleSheet.create({
    contextHeaderWrapper: {
        flexDirection: "row",
        minBlockSize: "auto",
    },
});
